import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useWorkbenchContext } from './WorkbenchContext';

// 创建认证上下文
const AuthContext = createContext();

// API基础URL - 本地开发服务器
// 如果环境变量已包含/api，则直接使用；否则添加/api后缀
const BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100');
const API_URL = BASE_URL.endsWith('/api/v2')
  ? BASE_URL
  : BASE_URL.endsWith('/api')
    ? `${BASE_URL}/v2`
    : `${BASE_URL}/api/v2`;
const PENDING_MEMBERSHIP_INVITE_CODE_KEY = 'pendingMembershipInviteCode';
const MEMBERSHIP_INVITE_APPLYING_KEY = 'membershipInviteApplying';

// 认证提供者组件
export const AuthProvider = ({ children }) => {
  const workbench = useWorkbenchContext();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const applyPendingMembershipInviteCode = async (token) => {
    const inviteCode = localStorage.getItem(PENDING_MEMBERSHIP_INVITE_CODE_KEY);
    if (!token || !inviteCode) return;
    if (localStorage.getItem(MEMBERSHIP_INVITE_APPLYING_KEY) === '1') return;

    try {
      localStorage.setItem(MEMBERSHIP_INVITE_APPLYING_KEY, '1');
      const response = await axios.post(
        `${API_URL}/membership/invite-by-code`,
        { invite_code: inviteCode },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data?.success) {
        localStorage.removeItem(PENDING_MEMBERSHIP_INVITE_CODE_KEY);
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 404) {
        localStorage.removeItem(PENDING_MEMBERSHIP_INVITE_CODE_KEY);
      }
      console.error('应用待领取邀请码失败:', error.message);
    } finally {
      localStorage.removeItem(MEMBERSHIP_INVITE_APPLYING_KEY);
    }
  };

  // 初始化验证用户登录状态
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (token) {
      // 先恢复缓存，避免页面闪烁；随后必须以服务器资料为准重新校准。
      // 历史账号可能同时保留“待绑定学校”和真实学校，永久使用旧缓存会
      // 让已经修正的主学校仍卡在浏览器本地。
      try {
        if (userStr) {
          const userData = JSON.parse(userStr);
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('解析用户信息失败:', error);
      }
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  // 获取当前用户信息
  const fetchCurrentUser = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const freshUser = response.data.data?.user || response.data.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
        setIsAuthenticated(true);
        applyPendingMembershipInviteCode(token);
      } else {
        // 如果验证失败，清除token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error.message);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // 登录
  const login = async (username, password, verification = {}) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { username, password, ...verification });
      
      if (response.data.success) {
        const { token, user } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        applyPendingMembershipInviteCode(token);
        return { success: true, user };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('登录请求失败:', error.message);
      return { 
        success: false, 
        message: error.response?.data?.message || '登录失败，请稍后重试',
        captchaRequired: error.response?.data?.captchaRequired
      };
    }
  };

  // 注销
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');  
    setUser(null);
    setIsAuthenticated(false);
  };

  // 直接设置用户登录状态（用于微信扫码登录等场景）
  const setUserLogin = (userData, token) => {
    if (token) {
      localStorage.setItem('token', token);
    }
    
    // 保存用户信息到 localStorage
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    applyPendingMembershipInviteCode(token);
  };

  // 检查是否是超级管理员
  const isSuperAdmin = () => {
    return isAuthenticated && user && (
      user.role === 'platform_admin' ||
      user.role === 'super_admin' ||
      user.role === 'admin' ||
      user.username === 'admin'
    );
  };

  // 检查是否是学校管理员
  const isSchoolAdmin = () => {
    return isAuthenticated && user && user.role === 'school_admin';
  };

  // 提供的上下文值
  // Only the embedded view's default school changes; storage and server identity stay authoritative.
  const viewUser = useMemo(() => workbench && user ? { ...user, schoolId: workbench.schoolId, schoolName: workbench.schoolName, workbenchSchoolId: workbench.schoolId } : user, [workbench, user]);
  const contextValue = {
    user: viewUser,
    currentUser: viewUser,
    isAuthenticated,
    loading,
    login,
    logout,
    setUserLogin,
    isSuperAdmin,
    isSchoolAdmin
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义hook，用于在组件中访问认证上下文
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 
